/**
 * OAuth Callback Page for Calendar Sync
 *
 * Handles the redirect from Google/Microsoft OAuth.
 * Exchanges the authorization code with the backend via the shared api
 * client, notifies the opener via BroadcastChannel, then closes the window.
 * Mirrors the contact-book SyncCallbackPage so both arches behave the same.
 */

import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { SYNC_OAUTH_CHANNEL } from '../hooks/useSyncConnections';

const CALENDAR_ARCHE_PATH = '/a/arche_calendar';

/**
 * Leave the callback route after a failure.
 *
 * `window.close()` only works for a script-opened window, so it is a silent
 * no-op when the OAuth redirect replaced the current tab — which is exactly
 * how the user got stranded. Attempt the close, then fall back to navigating
 * back into the arche if we are still here a tick later.
 */
function handleDismiss(): void {
  window.close();
  window.setTimeout(() => {
    window.location.replace(CALENDAR_ARCHE_PATH);
  }, 150);
}

export function SyncCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    // Prevent double-call from React strict mode (state is single-use)
    if (calledRef.current) return;
    calledRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setErrorMessage(error === 'access_denied' ? 'Access was denied.' : error);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setErrorMessage('Missing authorization code or state.');
      return;
    }

    api
      .calendarSyncOAuthCallback(code, state)
      .then(() => {
        setStatus('success');

        // Notify opener that OAuth completed; useSyncConnections listens.
        const channel = new BroadcastChannel(SYNC_OAUTH_CHANNEL);
        channel.postMessage({ type: 'oauth-complete' });
        channel.close();

        setTimeout(() => {
          window.close();
        }, 1500);
      })
      .catch((err) => {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to connect account.');
      });
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4 p-8">
        {status === 'loading' && (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">Connecting your account...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="h-8 w-8 mx-auto text-green-500" />
            <p className="font-medium">Account connected!</p>
            <p className="text-sm text-muted-foreground">This window will close automatically.</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-8 w-8 mx-auto text-destructive" />
            <p className="font-medium">Connection failed</p>
            <p className="text-sm text-muted-foreground max-w-sm break-words">{errorMessage}</p>
            {/* The success path self-closes; the error path used to render
                nothing else, stranding the user on a route with no navigation
                — the only escape was reopening the arche. */}
            <Button variant="outline" onClick={handleDismiss}>
              Back to Calendar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
